import React, { useState } from "react";

// Tier order — display top-down.
const TIER_META = [
  { key: "exec",     label: "Executives",   color: "#7c3aed" },
  { key: "director", label: "Directors",    color: "#2563eb" },
  { key: "manager",  label: "Managers",     color: "#0891b2" },
  { key: "ic",       label: "Contributors", color: "#6b7280" }
];

const initials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[parts.length - 1]?.[0] || "")).toUpperCase();
};

const groupByTier = (entries) => {
  const map = { exec: [], director: [], manager: [], ic: [] };
  for (const e of entries) {
    const t = TIER_META.find(t => t.key === e.tier) ? e.tier : "ic";
    map[t].push(e);
  }
  return map;
};

const tierLabel = (key) => (TIER_META.find(t => t.key === key) || {}).label || "Contributor";

// theorg.com SERP rows sometimes surface the company itself as a "person"
// ("Palo Alto Networks — HQ"). Keep only rows that look like real people: a
// 2+ word name that doesn't end in a corporate suffix, and not an "HQ" label.
// Backstops the server-side filter and cleans already-stored research.
const COMPANY_SUFFIX_RE = /\b(inc|incorporated|corp|corporation|llc|ltd|limited|co|company|networks|technologies|technology|solutions|systems|group|labs|software|holdings|partners|industries|international|ventures|capital|communications|consulting|enterprises|associates|global|worldwide|digital|media|health|financial|bancorp|bank|pharmaceuticals|therapeutics|biosciences)\.?$/i;

const isRealPerson = (e) => {
  const name = String(e?.name || "").trim();
  const title = String(e?.title || "").trim();
  if (name.split(/\s+/).filter(Boolean).length < 2) return false;
  if (COMPANY_SUFFIX_RE.test(name)) return false;
  if (/^(hq|headquarters)$/i.test(title)) return false;
  return true;
};

const ContactCard = ({ person, accent, onPick, isFocused }) => {
  return (
    <button
      type="button"
      className={`drOrg__card ${isFocused ? "drOrg__card--focus" : ""}`}
      onClick={() => onPick && onPick(person)}
      title={`${person.name} — ${person.title || ""}`.trim()}
    >
      <div className="drOrg__cardTop">
        <div className="drOrg__avatar" style={{ background: accent }}>
          {initials(person.name)}
        </div>
        <div className="drOrg__cardMeta">
          <div className="drOrg__name">{person.name}</div>
          {person.title && <div className="drOrg__title">{person.title}</div>}
        </div>
      </div>
      <div className="drOrg__cardFooter">
        <span
          className="drOrg__tierBadge"
          style={{ color: accent, borderColor: `${accent}33`, background: `${accent}0d` }}
        >
          <span className="drOrg__tierBadgeDot" style={{ background: accent }} />
          {tierLabel(person.tier)}
        </span>
        <div className="drOrg__cardActions" aria-hidden="true">
          <i className="material-icons-outlined drOrg__cardActionIcon">info</i>
        </div>
      </div>
    </button>
  );
};

const TierRow = ({ tier, people, focused, onPick }) => {
  if (!people || people.length === 0) return null;
  return (
    <div className="drOrg__tier">
      <div className="drOrg__tierLabel" style={{ color: tier.color }}>
        <span className="drOrg__tierDot" style={{ background: tier.color }} />
        {tier.label}
        <span className="drOrg__tierCount">{people.length}</span>
      </div>
      <div className="drOrg__cards">
        {people.map((p, i) => (
          <ContactCard
            key={i}
            person={p}
            accent={tier.color}
            onPick={onPick}
            isFocused={focused && focused.name === p.name && focused.title === p.title}
          />
        ))}
      </div>
    </div>
  );
};

const OrgChart = ({ orgChart }) => {
  const [focused, setFocused] = useState(null);
  const [open, setOpen] = useState(true);

  if (!Array.isArray(orgChart) || orgChart.length === 0) return null;

  // Drop company-name-as-person / non-person rows before grouping, so the
  // chart only ever shows real people — including for already-stored research.
  const people = orgChart.filter(isRealPerson);
  if (people.length === 0) return null;

  const grouped = groupByTier(people);
  const total = people.length;

  return (
    <div className="drSection drOrg">
      <button
        type="button"
        className="drOrg__header"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <i className="material-icons-outlined drOrg__chevron">
          {open ? "expand_more" : "chevron_right"}
        </i>
        <span className="drSectionIcon">🏛</span>
        <h3 className="drOrg__title">Org Chart</h3>
        <span className="drOrg__count">{total}</span>
      </button>

      {open && (
        <>
          <div className="drOrg__legend">
            Click any contact for details. Tier inferred from title — use as starting context, not authoritative reporting structure.
          </div>
          <div className="drOrg__chart">
            {TIER_META.map((tier, idx) => {
              const hasNextTier = TIER_META.slice(idx + 1)
                .some(t => (grouped[t.key] || []).length > 0);
              return (
                <React.Fragment key={tier.key}>
                  <TierRow
                    tier={tier}
                    people={grouped[tier.key]}
                    focused={focused}
                    onPick={setFocused}
                  />
                  {(grouped[tier.key] || []).length > 0 && hasNextTier && (
                    <div className="drOrg__connectorRow" aria-hidden="true">
                      <div className="drOrg__connectorLine" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {focused && (
            <div className="drOrg__detail">
              <div className="drOrg__detailHeader">
                <div className="drOrg__detailHeading">
                  <div className="drOrg__detailAvatar">
                    {initials(focused.name)}
                  </div>
                  <div>
                    <div className="drOrg__detailName">{focused.name}</div>
                    <div className="drOrg__detailTitle">{focused.title}</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="drOrg__detailClose"
                  onClick={() => setFocused(null)}
                  aria-label="Close detail"
                >
                  ×
                </button>
              </div>
              {focused.snippet && (
                <p className="drOrg__detailSnippet">{focused.snippet}</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OrgChart;
